import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    getMyServicemenProfile,
    updateServicemen,
    clearServicemenMessages,
} from "../redux/slices/servicemenSlice";

import { fetchCategories } from "../redux/slices/categorySlice";
import api from "../services/api";

const EditServicemenForm = () => {
    const dispatch = useDispatch();

    const { myProfile, loading, successMessage, errorMessage } = useSelector(
        (state) => state.servicemen
    );

    const { categories } = useSelector((state) => state.categories);

    const { user } = useSelector((state) => state.auth);

    // FORM STATE
    const [form, setForm] = useState({
        contactNumber: "",
        state: "",
        city: "",
        address: "",
        aadharNumber: "",
        about: "",
        category: "",
    });

    const [statesList] = useState([
        "Haryana",
        "Punjab",
        "Delhi",
        "UP",
        "Rajasthan",
        "Bihar",
        "Himachal Pardesh",
    ]);

    const [citiesList, setCitiesList] = useState([]);

    const [profileImage, setProfileImage] = useState(null);
    const [aadharImage, setAadharImage] = useState(null);

    // FETCH PROFILE
    useEffect(() => {
        if (user?._id) dispatch(getMyServicemenProfile(user._id));
    }, [user]);

    // FETCH CATEGORIES ON MOUNT
    useEffect(() => {
        dispatch(fetchCategories({ page: 1, limit: 100 }));
    }, []);

    // SET PROFILE DATA TO FORM
    useEffect(() => {
        if (myProfile) {
            setForm({
                contactNumber: myProfile.contactNumber || "",
                state: myProfile.state || "",
                city: myProfile.city || "",
                address: myProfile.address || "",
                aadharNumber: myProfile.aadharNumber || "",
                about: myProfile.about || "",
                category: myProfile.category?._id || "",
            });

            if (myProfile.state) fetchCities(myProfile.state);
        }
    }, [myProfile]);

    const fetchCities = async (selectedState) => {
        try {
            const { data } = await api.get(`/search-cities?state=${selectedState}`);
            setCitiesList(data?.data || []);
        } catch (err) {
            console.log("City Fetch Error:", err);
        }
    };

    useEffect(() => {
        if (form.state) fetchCities(form.state);
    }, [form.state]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // SUBMIT
    const handleSubmit = (e) => {
        e.preventDefault();

        const fd = new FormData();
        Object.keys(form).forEach((key) => fd.append(key, form[key]));

        if (profileImage) fd.append("profileImage", profileImage);
        if (aadharImage) fd.append("aadharImage", aadharImage);

        Swal.fire({
            title: "Updating...",
            text: "Please wait",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        dispatch(updateServicemen({ id: myProfile._id, formData: fd }));
    };

    // SWEET ALERT
    useEffect(() => {
        if (successMessage) {
            Swal.fire("Success", successMessage, "success");
            dispatch(clearServicemenMessages());
        }
        if (errorMessage) {
            Swal.fire("Error", errorMessage, "error");
            dispatch(clearServicemenMessages());
        }
    }, [successMessage, errorMessage]);

    if (!myProfile) return <p className="text-center mt-4">Loading profile...</p>;

    return (
        <div className="container mt-4">
            <div className="card shadow">
                <div className="card-body">
                    <h3 className="mb-3">Edit Servicemen Profile</h3>

                    <form onSubmit={handleSubmit}>

                        {/* CATEGORY DROPDOWN */}
                        <div className="mb-3">
                            <label className="form-label">Select Category</label>
                            <select
                                className="form-control"
                                name="category"
                                value={form.category}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Category</option>

                                {categories?.map((cat) => (
                                    <option key={cat._id} value={cat._id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* OTHER FIELDS BELOW... */}
                        {/* Contact Number */}
                        <div className="mb-3">
                            <label className="form-label">Contact Number</label>
                            <input
                                type="text"
                                className="form-control"
                                name="contactNumber"
                                value={form.contactNumber}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {/* State */}
                        <div className="mb-3">
                            <label className="form-label">State</label>
                            <select
                                className="form-control"
                                name="state"
                                value={form.state}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select State</option>
                                {statesList.map((item, i) => (
                                    <option key={i} value={item}>
                                        {item}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* City */}
                        <div className="mb-3">
                            <label className="form-label">City</label>
                            <select
                                className="form-control"
                                name="city"
                                value={form.city}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select City</option>
                                {citiesList.map((item, i) => (
                                    <option key={i} value={item.city}>
                                        {item.city}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Address */}
                        <div className="mb-3">
                            <label className="form-label">Address</label>
                            <textarea
                                className="form-control"
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                rows="2"
                                required
                            ></textarea>
                        </div>

                        {/* Aadhar */}
                        <div className="mb-3">
                            <label className="form-label">Aadhar Number</label>
                            <input
                                type="text"
                                className="form-control"
                                name="aadharNumber"
                                value={form.aadharNumber}
                                onChange={handleChange}
                                required
                                disabled
                            />
                        </div>

                        {/* About */}
                        <div className="mb-3">
                            <label className="form-label">About</label>
                            <textarea
                                className="form-control"
                                name="about"
                                value={form.about}
                                onChange={handleChange}
                                rows="3"
                                required
                            ></textarea>
                        </div>

                        {/* IMAGES SAME AS BEFORE */}
                        {/* Profile Image */}
                        <div className="mb-3">
                            <label className="form-label">Profile Image</label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={(e) => setProfileImage(e.target.files[0])}
                                accept="image/*"

                            />
                            {myProfile.profileImage && (
                                <img
                                    src={myProfile.profileImage}
                                    width="100"
                                    className="mt-2 rounded"
                                />
                            )}
                        </div>

                        {/* Aadhar Image */}
                        <div className="mb-3">
                            <label className="form-label">Aadhar Image</label>
                            <input
                                type="hidden"
                                className="form-control"
                                onChange={(e) => setAadharImage(e.target.files[0])}
                                accept="image/*"
                                disabled
                            />
                            {myProfile.aadharImage && (
                                <img
                                    src={myProfile.aadharImage}
                                    width="100"
                                    className="mt-2 rounded"
                                />
                            )}
                        </div>

                        <button className="btn btn-primary w-100">
                            {loading ? "Updating..." : "Update Profile"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditServicemenForm;
